import type { TeacherLoadBucketRange } from "@/lib/dashboard/teacher-load";
import type { StudioOperationsSettings } from "./operations-settings-defaults";

export function buildTeacherLoadBucketRanges(
  settings: Pick<
    StudioOperationsSettings,
    "teacherLoadLightMax" | "teacherLoadPartTimeMax" | "teacherLoadFullTimeMax"
  >,
): TeacherLoadBucketRange[] {
  const lightMax = settings.teacherLoadLightMax;
  const partTimeMax = settings.teacherLoadPartTimeMax;
  const fullTimeMax = settings.teacherLoadFullTimeMax;

  return [
    { id: "light", min: 0, max: lightMax },
    { id: "part-time", min: lightMax + 1, max: partTimeMax },
    { id: "full-time", min: partTimeMax + 1, max: fullTimeMax },
    { id: "heavy", min: fullTimeMax + 1, max: null },
  ];
}

export function getTeacherLoadOverworkedThreshold(
  settings: Pick<StudioOperationsSettings, "teacherLoadFullTimeMax">,
): number {
  return settings.teacherLoadFullTimeMax + 1;
}

export function formatTeacherLoadBucketRangeLabel(
  range: TeacherLoadBucketRange,
): string {
  const suffix = " sessions/week";

  switch (range.id) {
    case "light":
      return `Light · 0–${range.max}${suffix}`;
    case "part-time":
      return `Part-time · ${range.min}–${range.max}${suffix}`;
    case "full-time":
      return `Full-time · ${range.min}–${range.max}${suffix}`;
    case "heavy":
      return `Heavy · ${range.min}+${suffix}`;
    default:
      break;
  }

  if (range.max == null) {
    return `${range.min}+${suffix}`;
  }
  if (range.min === 0) {
    return `0–${range.max}${suffix}`;
  }
  return `${range.min}–${range.max}${suffix}`;
}

export function formatTeacherLoadBucketRangeTitle(
  range: TeacherLoadBucketRange,
): string {
  switch (range.id) {
    case "light":
      return `Light load · 0–${range.max} weekly sessions`;
    case "part-time":
      return `Part-time load · ${range.min}–${range.max} weekly sessions`;
    case "full-time":
      return `Full-time load · ${range.min}–${range.max} weekly sessions`;
    case "heavy":
      return `Heavy load · ${range.min}+ weekly sessions`;
    default:
      break;
  }

  if (range.max == null) {
    return `Teachers with ${range.min}+ weekly sessions`;
  }
  if (range.min === 0) {
    return `Teachers with 0–${range.max} weekly sessions`;
  }
  return `Teachers with ${range.min}–${range.max} weekly sessions`;
}
